import React, { useEffect, useMemo, useState } from "react";
import * as S from "./PageWrapper.style";
import { UpgradeButton } from "./UpgradeButton";
import { TryForFreeBanner } from "./TryForFreeBanner";
import { Notifications } from "~/components/notifications";
import { useMediaQuery } from "~/hooks/useMediaQuery";
import { PlansModal } from "~/components/plans/plansModal";
import { ResumeModal } from "~/components/resumeModal";
import { LimitsModal } from "~/components/limits-modal";
import { ReconnectAliexpressBanner } from "../reconnect-aliexpress-banner";
import { useAccount } from "~/hooks/useAccount";
import { Onboarding } from "../onboarding";
import {
  DISABLE_PAYMENTS,
  clearRecentPayment,
  hasRecentPayment,
} from "~/helpers/plans";
import { logger } from "~/helpers/logger";
import { usePlans } from "~/api/billing/queries";
import { paths } from "~/router/paths";
import { SubscriptionType } from "~/types/billing";
import { PlanStatus } from "~/types/account";
import { Userpilot } from "userpilot";
import { useLocation, useNavigate } from "react-router-dom";
import { ModalConnectToStore } from "../connect-to-store/modal";
import { BottomMenu } from "../bottom-menu";
import { AnualPlansModal } from "../plans/anualPlansModal";
import { DTPromotionModal } from "../dt-promotion-modal";
import { PastDueModal } from "~/components/pastDueModal";
// import { SkipTrialBanner } from "~/components/skip-trial-banner";

interface IProps {
  children: React.ReactNode;
}

export const PageWrapper: React.FC<IProps> = ({ children = null }) => {
  const { account, getAccount } = useAccount();
  const pageLocation = useLocation();
  const isSettingsPage = pageLocation.pathname.includes("settings");
  const isNotMobile = useMediaQuery("tablet");
  const queryParams = new URLSearchParams(pageLocation.search);
  // Start as false so content renders immediately; set to true if onboarding is needed.
  // This eliminates the blocking full-page loader that was delaying all page LCP.
  const [isShowingOnboarding, setIsShowingOnboarding] = useState(false);
  const planId = queryParams.get("plan_id");
  const showRating = queryParams.get("show_rating");
  const onboarding = queryParams.get("onboarding");
  const annual = queryParams.get("annual");
  const { data: plans, refetch: refetchPlans } = usePlans();
  const activePlans = plans?.filter(
    (plan) => plan.status === PlanStatus.ACTIVE
  );
  const navigate = useNavigate();

  const resolveCheckoutPlanId = (
    plansList: typeof activePlans
  ): string | undefined =>
    plansList?.find(
      (plan) =>
        plan.name === "Empire" &&
        plan.interval === SubscriptionType.MONTHLY &&
        plan.for_winning
    )?.id ?? plansList?.[0]?.id;

  const handleGoToApp = () => {
    setIsShowingOnboarding(false);
  };

  useEffect(() => {
    Userpilot.reload();
    if (pageLocation.search.includes("onboarding=true")) {
      localStorage.setItem("isOnboarding", "true");
    }
  }, [pageLocation]);

  useEffect(() => {
    const checkOnboardingCondition = async () => {
      // Use account already in context (from Sidebar's earlier fetch) when possible
      // to avoid a redundant blocking API round-trip on every navigation.
      const accountData = account || await getAccount();
      if (!accountData) return;

      // A successful Stripe payment can precede the subscription webhook by
      // seconds, so a just-paid account may still read as unsubscribed here.
      // During that grace window, don't route the user back into onboarding/
      // checkout (post-payment redirect loop).
      if (accountData.active_subscription) {
        clearRecentPayment();
      }

      const isAwaitingSubscriptionSync =
        !accountData.active_subscription && hasRecentPayment();

      if (
        onboarding &&
        !accountData.active_subscription &&
        !isAwaitingSubscriptionSync
      ) {
        return setIsShowingOnboarding(true);
      }

      const searchParams = new URLSearchParams(window.location.search);

      if(annual) {
        searchParams.set("annual", "true");
        navigate(`?${searchParams.toString()}`);
      }

      if (
        accountData &&
        !accountData?.active_subscription &&
        !accountData?.has_subscribed_before &&
        !isAwaitingSubscriptionSync &&
        !planId &&
        !showRating
      ) {
        searchParams.set("onboarding", "true");
        searchParams.set("origin", accountData?.signup_origin ?? "direct");
        navigate(`?${searchParams.toString()}`, { replace: true });
        setIsShowingOnboarding(true);
        return;
      }

      const showCustomizationModal = accountData.show_store_customization;

      if (showCustomizationModal) {
        searchParams.set("onboarding", "true");
        searchParams.set("origin", accountData?.signup_origin ?? "direct");
        navigate(`?${searchParams.toString()}`);
        setIsShowingOnboarding(true);
        return;
      }

      searchParams.delete("onboarding");
      const updatedQueryParams = searchParams.toString();
      const newUrl =
        window.location.pathname +
        (updatedQueryParams ? `?${updatedQueryParams}` : "");
      window.history.replaceState(null, "", newUrl);

      setIsShowingOnboarding(false);
    };

    checkOnboardingCondition().catch((error) => logger.error("Failed to check onboarding condition", error));
  }, [planId, onboarding, account]);

  return (
    <>
      {!isShowingOnboarding ? (
        <S.PageWrapper
          padding={isSettingsPage ? "24px 20px 58px" : "24px 92px 58px"}
          style={{
            backgroundColor: "#f7f7f7",
          }}>
          <S.PageHeader>
            <UpgradeButton />
            {isNotMobile ? <Notifications /> : null}
          </S.PageHeader>
          <ReconnectAliexpressBanner />
          <PlansModal />
          <DTPromotionModal />
          <AnualPlansModal />
          <ResumeModal />
          <LimitsModal />
          <ModalConnectToStore />
          <PastDueModal />
          {/* <SkipTrialBanner /> */}

          <S.PageMaxWidth>
            <S.BannerSlot>
              {["aliexpress-products"].includes(
                window.location.pathname.split("/")[1]
              ) && <TryForFreeBanner />}
            </S.BannerSlot>
            {children}

            <BottomMenu />
          </S.PageMaxWidth>
        </S.PageWrapper>
      ) : (
        <Onboarding
          isShowing={isShowingOnboarding}
          onFinish={async () => {
            if (DISABLE_PAYMENTS) {
              setIsShowingOnboarding(false);
              return;
            }

            let selectedPlanId = resolveCheckoutPlanId(activePlans);

            if (!selectedPlanId) {
              const { data: freshPlans } = await refetchPlans();
              const freshActivePlans = freshPlans?.filter(
                (plan) => plan.status === PlanStatus.ACTIVE
              );
              selectedPlanId = resolveCheckoutPlanId(freshActivePlans);
            }

            if (!selectedPlanId) {
              setIsShowingOnboarding(false);
              navigate(paths.settings.plans);
              return;
            }

            setIsShowingOnboarding(false);
            navigate(paths.checkout.index, {
              state: { selectedPlanId },
            });
          }}
          onGoToApp={handleGoToApp}
        />
      )}
    </>
  );
};

export const PageWrapperFullScreen: React.FC<IProps> = ({
  children = null,
}) => {
  const pageLocation = useLocation();

  useEffect(() => {
    Userpilot.reload();
  }, [pageLocation]);

  return (
    <S.PageWrapperFullscreen>
      <PlansModal />
      <AnualPlansModal />
      <ResumeModal />
      <LimitsModal />
      <PastDueModal />
      <>{children}</>
    </S.PageWrapperFullscreen>
  );
};
